import uuid from 'uuid/v4';
import noop from 'lodash/noop';

import * as modalCodes from '../../messageSystem/constants/modalCodes';
import createAction from '../actions';
import { removeModal } from './removeModal';
import { getCurrentModal } from './getCurrentModal';

export const ADD_MODAL = createAction('ADD_MODAL');
export const PREVENT_DUPLICATE_MODAL = createAction('PREVENT_DUPLICATE_MODAL');
export const PREVENT_MODAL_NOT_ALL_TRUE = createAction('PREVENT_MODAL_NOT_ALL_TRUE');
export const ADD_MODAL_FORCE = createAction('ADD_MODAL_FORCE');

const handleFn = (handleName, id) => (dispatch) => (...props) => {
  dispatch(removeModal(id));
  handleName(...props);
};

/**
 * [addDefaultModal description]
 *
 * @param {string|undefined} heading - header message for Default container
 * @param {string|undefined} body - body message for Default container
 * @param {Object|undefined} bodyValues
 * @param {string|undefined} buttonMessageId - button message for Default container
 * @param {function} onOk - success behavior
 * @param {function} onDismiss - dismiss behavior (click in overlay layer)
 * @param {boolean} preventClosingWhenDismiss - prevent closing of modal when clicked in a dismiss area
 */
export const addDefaultModal = ({
  heading,
  body,
  bodyValues,
  buttonMessageId,
  onOk = noop,
  onDismiss = noop,
  preventClosingWhenDismiss = false,
}) => (dispatch) => {
  const id = uuid();

  dispatch({
    type: ADD_MODAL,
    meta: {
      id,
      typeId: modalCodes.DEFAULT_MODAL,
      heading,
      body,
      bodyValues,
      buttonMessageId,
      onOk: handleFn(onOk, id)(dispatch),
      onDismiss: preventClosingWhenDismiss ? onDismiss : handleFn(onDismiss, id)(dispatch),
    },
  });

  dispatch(getCurrentModal());
};

/**
 * [addModal description]
 *
 * @param {string} testId - data-testId for modal window
 * @param {number} typeId - text that would be displayed in header
 * @param {number|undefined} autoDismissTime - time before Modal closed
 * @param {string|undefined} heading - header message for Default container
 * @param {string|undefined} body - body message for Default container
 * @param {Object|undefined} bodyValues
 * @param {string|undefined} buttonMessageId - button message for Default container
 * @param {boolean} canAddDuplicate - can add to queue modals with the same Id
 * @param {Array|undefined} allTrue - object with params that should be true for show modal
 * @example
 *          { a: true, b: true } -> true,
 *          { a: true, b: false, c: true} -> false,
 *          { a: true, b: undefined } -> false,
 *          {} -> true,
 *          undefined -> true
 * @param {boolean} isForce - if param is true shows modal on the top even other modal already opened
 * @param {function} onOk - success behavior
 * @param {function} onCancel - failure behavior
 * @param {function} onDismiss - dismiss behavior (click in overlay layer)
 * @param {boolean} preventClosingWhenDismiss - prevent closing of modal when clicked in a dismiss area
 */
export const addModal = ({
    testId = '',
    typeId = modalCodes.DEFAULT_MODAL,
    autoDismissTime,
    onAutoDismissTime,
    heading,
    body,
    bodyValues,
    buttonMessageId,
    canAddDuplicate = true,
    allTrue,
    isForce = false,
    onOk = noop,
    onCancel = noop,
    onDismiss = noop,
    preventClosingWhenDismiss = false,
  }) => (dispatch, getState) => {
    const queue = getState().messageSystem.modals.queue;
    const id = uuid();

    if (!canAddDuplicate && queue.some(element => element.typeId === typeId)) {
      dispatch({
        type: PREVENT_DUPLICATE_MODAL,
        meta: {
          testId,
          id,
          typeId,
          autoDismissTime,
          heading,
          body,
          bodyValues,
          buttonMessageId,
          canAddDuplicate,
          allTrue,
          isForce,
         },
      });
      return;
    }

    if (allTrue && !Object.keys(allTrue).every(element => allTrue[element])) {
      dispatch({
        type: PREVENT_MODAL_NOT_ALL_TRUE,
        meta: {
          testId,
          id,
          typeId,
          autoDismissTime,
          heading,
          body,
          bodyValues,
          buttonMessageId,
          canAddDuplicate,
          allTrue,
          isForce,
        },
      });
      return;
    }
    dispatch({
      type: isForce ? ADD_MODAL_FORCE : ADD_MODAL,
      meta: {
        testId,
        id,
        typeId,
        autoDismissTime,
        heading,
        body,
        bodyValues,
        buttonMessageId,
        canAddDuplicate,
        allTrue,
        isForce,
        onOk: handleFn(onOk, id)(dispatch),
        onCancel: handleFn(onCancel, id)(dispatch),
        onDismiss: preventClosingWhenDismiss ? onDismiss : handleFn(onDismiss, id)(dispatch),
        onAutoDismissTime: handleFn(onAutoDismissTime || noop, id)(dispatch),
      },
    });

    dispatch(getCurrentModal());
  };
